import noop from 'lodash/noop'
import React, { Ref, RefCallback, useCallback, useContext, useMemo, useRef, useState } from 'react'

type GroupVisibility = Record<string, boolean>

interface ScrollPosition {
  groupVisibility: GroupVisibility
  updateGroupVisibility: (group: string, isVisible: boolean) => void
}

export const ScrollPositionContext = React.createContext<ScrollPosition>({
  groupVisibility: {},
  updateGroupVisibility: noop,
})

export const ScrollPositionProvider = ({ children }: { children?: React.ReactNode }) => {
  const [groupVisibility, setGroupVisibility] = useState<GroupVisibility>({})
  const updateGroupVisibility = useCallback((group: string, isVisible: boolean) => {
    setGroupVisibility((prev) => {
      if (prev?.[group] === isVisible) {
        return prev
      }

      return {
        ...prev,
        [group]: isVisible,
      }
    })
  }, [])

  const value = useMemo(() => ({ groupVisibility, updateGroupVisibility }), [groupVisibility, updateGroupVisibility])

  return (
    <ScrollPositionContext.Provider value={value}>
      {children}
    </ScrollPositionContext.Provider>
  )
}

export const useGroupVisibility = (group: string): boolean | undefined => {
  const { groupVisibility } = useContext(ScrollPositionContext)
  const visibility = groupVisibility[group]
  return useMemo(() => visibility, [visibility])
}

export const useGroupVisibilityRef = (group: string): RefCallback<Element> => {
  const { updateGroupVisibility } = useContext(ScrollPositionContext)
  const intersectionObserver = useRef<IntersectionObserver | null>(null)

  const ref: Ref<HTMLElement | null> = useCallback((element: HTMLElement | null) => {
    if (typeof IntersectionObserver === 'undefined') {
      return
    }

    if (intersectionObserver.current) {
      intersectionObserver.current.disconnect()
    }

    intersectionObserver.current = new IntersectionObserver(
      ([entry]) => updateGroupVisibility(group, entry.isIntersecting),
    )

    if (element) {
      intersectionObserver.current.observe(element)
    }
  }, [group, updateGroupVisibility])

  return ref
}
